import { useState, useEffect, useRef } from 'react';
import { api, API_KEY } from './SecretaryPortal';

// ─── Email composer ───────────────────────────────────────────────────────────
//
// Subject, message, the secretary's saved templates (their own, not shared)
// and an optional PDF (e.g. final instructions with a paddock map). Used by
// the accept/reject dialog (SubmissionActionDialog.jsx) and the email-all-
// entrants screen (EmailEntrantsDialog.jsx), so both behave the same.
//
// useEmailComposer(sessionToken, initialSubject) returns the subject/body
// state and setters (callers set their own default text), emailPayload()
// for the request's `email` part ({subject, body, template_id | attachment},
// see _email_attachment in secretary_routes.py), and renderComposer() for
// the fields themselves.

// Matches TEMPLATE_PDF_MAX_BYTES in secretary_routes.py.
const PDF_MAX_BYTES = 5 * 1024 * 1024;

export function useEmailComposer(sessionToken, initialSubject = '') {
  const [templates, setTemplates]       = useState([]);
  const [emailSubject, setEmailSubject] = useState(initialSubject);
  const [emailBody, setEmailBody]       = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [newTemplateLabel, setNewTemplateLabel] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  // The PDF going with this email: null, the chosen template's stored PDF
  // ({source: 'template', templateId, filename}), or a file picked here
  // ({source: 'file', filename, data (base64), file}).
  const [attachment, setAttachment] = useState(null);
  const [templateMsg, setTemplateMsg] = useState(null);
  const pdfInputRef = useRef(null);

  useEffect(() => {
    api('/api/secretary/email-templates', {}, sessionToken)
      .then(setTemplates)
      .catch(() => {}); // non-critical — dialog still works with the fallback text
  }, [sessionToken]);

  const applyTemplate = (id) => {
    const t = templates.find(t => t.id === Number(id));
    setTemplateMsg(null);
    if (t) {
      setSelectedTemplateId(String(t.id));
      setEmailSubject(t.subject);
      setEmailBody(t.body);
      setAttachment(t.attachment_filename ? { source: 'template', templateId: t.id, filename: t.attachment_filename } : null);
    }
  };

  const pickPdf = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setTemplateMsg(null);
    if (file.type && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setTemplateMsg('That file isn\u2019t a PDF.');
      return;
    }
    if (file.size > PDF_MAX_BYTES) {
      setTemplateMsg('That PDF is too big: the limit is 5 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const data = String(reader.result).split(',')[1] || '';
      setAttachment({ source: 'file', filename: file.name, data, file });
    };
    reader.onerror = () => setTemplateMsg('That file couldn\u2019t be read.');
    reader.readAsDataURL(file);
  };

  const viewPdf = async () => {
    if (!attachment) return;
    // Open the tab now, while the click still counts as the user's action,
    // or a pop-up blocker stops it once the fetch below has finished.
    const win = window.open('', '_blank');
    try {
      let blob;
      if (attachment.source === 'file') {
        blob = attachment.file;
      } else {
        const res = await fetch(`/api/secretary/email-templates/${attachment.templateId}/attachment`, {
          headers: { 'X-API-Key': API_KEY, Authorization: `Bearer ${sessionToken}` },
        });
        if (!res.ok) throw new Error('not found');
        blob = await res.blob();
      }
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      if (win) win.close();
      setTemplateMsg('The PDF couldn\u2019t be opened.');
    }
  };

  // The template's PDF part of a create/update request: a newly picked file
  // replaces it; removing it (\u2715) on an existing template removes it.
  const templateAttachmentFields = (existing) => {
    if (attachment?.source === 'file') return { attachment: { filename: attachment.filename, data: attachment.data } };
    if (!attachment && existing?.attachment_filename) return { remove_attachment: true };
    return {};
  };

  const afterTemplateSaved = (t) => {
    setSelectedTemplateId(String(t.id));
    setAttachment(t.attachment_filename ? { source: 'template', templateId: t.id, filename: t.attachment_filename } : null);
  };

  const saveAsTemplate = async () => {
    if (!newTemplateLabel.trim()) return;
    setTemplateMsg(null);
    try {
      const t = await api('/api/secretary/email-templates', {
        method: 'POST',
        body: JSON.stringify({ label: newTemplateLabel.trim(), subject: emailSubject, body: emailBody,
                               ...templateAttachmentFields(null) }),
      }, sessionToken);
      setTemplates(ts => [...ts, t].sort((a, b) => a.label.localeCompare(b.label)));
      afterTemplateSaved(t);
      setShowSaveTemplate(false);
      setNewTemplateLabel('');
      setTemplateMsg(`Saved as template \u201c${t.label}\u201d.`);
    } catch (err) {
      setTemplateMsg(err.message);
    }
  };

  const selectedTemplate = templates.find(t => String(t.id) === selectedTemplateId) || null;

  const updateTemplate = async () => {
    if (!selectedTemplate) return;
    if (!window.confirm(`Save the subject, message and PDF shown here over the template \u201c${selectedTemplate.label}\u201d?`)) return;
    setTemplateMsg(null);
    try {
      const t = await api(`/api/secretary/email-templates/${selectedTemplate.id}`, {
        method: 'PUT',
        body: JSON.stringify({ label: selectedTemplate.label, subject: emailSubject, body: emailBody,
                               ...templateAttachmentFields(selectedTemplate) }),
      }, sessionToken);
      setTemplates(ts => ts.map(x => (x.id === t.id ? t : x)));
      afterTemplateSaved(t);
      setTemplateMsg(`Template \u201c${t.label}\u201d updated.`);
    } catch (err) {
      setTemplateMsg(err.message);
    }
  };

  const deleteTemplate = async () => {
    if (!selectedTemplate) return;
    if (!window.confirm(`Delete the template \u201c${selectedTemplate.label}\u201d? This email\u2019s text stays as it is.`)) return;
    setTemplateMsg(null);
    try {
      await api(`/api/secretary/email-templates/${selectedTemplate.id}`, { method: 'DELETE' }, sessionToken);
      setTemplates(ts => ts.filter(x => x.id !== selectedTemplate.id));
      if (attachment?.source === 'template') setAttachment(null);
      setSelectedTemplateId('');
      setTemplateMsg(`Template \u201c${selectedTemplate.label}\u201d deleted.`);
    } catch (err) {
      setTemplateMsg(err.message);
    }
  };

  // The `email` part of an accept/reject request, with its PDF if any.
  const emailPayload = () => ({
    subject: emailSubject,
    body: emailBody,
    ...(attachment?.source === 'template' ? { template_id: attachment.templateId } : {}),
    ...(attachment?.source === 'file' ? { attachment: { filename: attachment.filename, data: attachment.data } } : {}),
  });

  const renderComposer = () => (
    <>
      {templates.length > 0 && (
        <div className="form-group">
          <label>Use a saved template</label>
          <select value={selectedTemplateId} onChange={e => applyTemplate(e.target.value)}>
            <option value="" disabled>Choose a template…</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.label}{t.attachment_filename ? ' 📎' : ''}</option>
            ))}
          </select>
        </div>
      )}
      <div className="form-group">
        <label>Subject</label>
        <input type="text" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} />
      </div>
      <div className="form-group">
        <label>Body</label>
        <textarea rows={6} value={emailBody} onChange={e => setEmailBody(e.target.value)}
                  style={{ resize: 'vertical', fontSize: '0.95rem' }} />
      </div>
      <div className="form-group">
        <label>PDF attachment</label>
        <input ref={pdfInputRef} type="file" accept="application/pdf,.pdf" onChange={pickPdf}
               style={{ display: 'none' }} />
        {attachment ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span>📎 <strong>{attachment.filename}</strong></span>
            <button className="btn-cancel" type="button" onClick={viewPdf}>View</button>
            <button className="btn-cancel" type="button" onClick={() => pdfInputRef.current?.click()}>Change…</button>
            <button className="btn-cancel" type="button" onClick={() => setAttachment(null)}
                    title="Don't attach a PDF to this email">✕ Remove</button>
          </div>
        ) : (
          <button className="btn-cancel" type="button" onClick={() => pdfInputRef.current?.click()}>
            📎 Attach a PDF…
          </button>
        )}
        <p className="muted-note" style={{ marginTop: '0.3rem' }}>
          Optional, up to 5 MB, e.g. final instructions with a paddock map.
          Saving this email as a template keeps the PDF with it.
        </p>
      </div>
      {showSaveTemplate ? (
        <div className="form-row" style={{ alignItems: 'flex-end' }}>
          <div className="form-group">
            <label>Template name</label>
            <input type="text" value={newTemplateLabel} onChange={e => setNewTemplateLabel(e.target.value)}
                   placeholder="e.g. Gurston June – acceptance" />
          </div>
          <button className="btn-save" onClick={saveAsTemplate} type="button">Save</button>
          <button className="btn-cancel" onClick={() => setShowSaveTemplate(false)} type="button">Cancel</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <button className="btn-cancel" onClick={() => setShowSaveTemplate(true)} type="button">
            Save as new template
          </button>
          {selectedTemplate && (
            <>
              <button className="btn-cancel" onClick={updateTemplate} type="button">
                Update “{selectedTemplate.label}”
              </button>
              <button className="btn-cancel" onClick={deleteTemplate} type="button">
                Delete “{selectedTemplate.label}”
              </button>
            </>
          )}
        </div>
      )}
      {templateMsg && <p className="muted-note" style={{ marginTop: '-0.5rem' }}>{templateMsg}</p>}
    </>
  );

  return { emailSubject, setEmailSubject, emailBody, setEmailBody, emailPayload, renderComposer };
}
